import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { checkoutAPI } from '../../lib/api';
import { formatPrice, formatDate, ORDER_STATUS } from '../../lib/utils';
import { trackPurchase } from '../../lib/tracking';

export default function OrderConfirmationPage() {
  const { orderId } = useParams();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrder();
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const response = await checkoutAPI.getOrder(orderId);
      setOrder(response.data);
      
      // Track purchase
      if (response.data.status === 'PAID') {
        trackPurchase({
          order_number: response.data.order_number,
          items: response.data.items,
          total: response.data.total_cents
        });
      }
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <StoreLayout>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-20 w-20 bg-muted rounded-full mx-auto" />
            <div className="h-8 bg-muted rounded w-2/3 mx-auto" />
            <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
          </div>
        </div>
      </StoreLayout>
    );
  }

  if (!order) {
    return (
      <StoreLayout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground mb-4">Commande introuvable</p>
          <Button asChild>
            <Link to="/">Retour à l'accueil</Link>
          </Button>
        </div>
      </StoreLayout>
    );
  }

  const statusInfo = ORDER_STATUS[order.status] || ORDER_STATUS.PENDING_PAYMENT;

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-muted/30 to-transparent">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6 ring-2 ring-green-200 dark:ring-green-800">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3 text-foreground">
              {t('checkout.success')}
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              {t('checkout.success.message')}
            </p>
          </motion.div>

          {/* Main Confirmation Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="border border-border rounded-xl p-6 sm:p-8 space-y-8 bg-card shadow-sm"
            data-testid="order-confirmation"
          >
            {/* Order Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-border">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Numéro de commande</p>
                <p className="font-mono font-bold text-xl text-foreground">{order.order_number}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${statusInfo.color} border border-current/20`}>
                  {statusInfo.label}
                </span>
              </div>
            </div>

            {/* Order Meta */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-6 border-b border-border">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Date de commande</p>
                <p className="text-foreground font-medium">{formatDate(order.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Email de confirmation</p>
                <p className="text-foreground font-medium">{order.email}</p>
              </div>
            </div>

            {/* Items Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Package className="h-5 w-5" />
                Articles commandés ({order.items.length})
              </h3>
              <div className="space-y-3 bg-muted/30 rounded-lg p-4">
                {order.items.map((item) => (
                  <div key={item.id} className="flex gap-4 p-3 bg-card rounded-lg border border-border hover:border-primary/50 transition-colors">
                    {/* Product Image */}
                    <div className="w-20 h-24 bg-muted rounded-lg overflow-hidden flex-shrink-0 ring-1 ring-border">
                      {item.product_image ? (
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    
                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground mb-1">{item.product_name}</p>
                      <p className="text-sm text-muted-foreground">Quantité: <span className="font-medium text-foreground">{item.quantity}</span></p>
                    </div>
                    
                    {/* Product Price */}
                    <div className="text-right">
                      <p className="font-mono font-semibold text-foreground whitespace-nowrap">
                        {formatPrice(item.price_cents * item.quantity)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatPrice(item.price_cents)} x {item.quantity}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="space-y-4 py-6 border-t border-border">
              <h3 className="text-lg font-semibold text-foreground">Adresse de livraison</h3>
              <div className="bg-muted/30 rounded-lg p-4 text-sm leading-relaxed text-foreground">
                <p className="font-medium mb-1">
                  {order.shipping_address.first_name} {order.shipping_address.last_name}
                </p>
                <p className="text-muted-foreground">{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && (
                  <p className="text-muted-foreground">{order.shipping_address.address_line2}</p>
                )}
                <p className="text-muted-foreground">
                  {order.shipping_address.postal_code} {order.shipping_address.city}
                </p>
                <p className="text-muted-foreground">{order.shipping_address.country}</p>
              </div>
            </div>

            {/* Tracking Info */}
            {order.tracking_number && (
              <div className="space-y-4 py-6 border-t border-border bg-muted/20 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Suivi de votre colis
                </h3>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Numéro de suivi :</p>
                  <p className="font-mono font-semibold text-foreground">{order.tracking_number}</p>
                </div>
                {order.tracking_url && (
                  <a 
                    href={order.tracking_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    Suivre mon colis
                    <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}

            {/* Order Totals */}
            <div className="space-y-3 pt-6 border-t border-border bg-gradient-to-br from-muted/40 to-muted/20 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Sous-total</span>
                <span className="font-medium text-foreground">{formatPrice(order.subtotal_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Livraison</span>
                <span className="font-medium text-foreground">
                  {order.shipping_cents === 0 ? (
                    <span className="text-green-600">Gratuite</span>
                  ) : (
                    formatPrice(order.shipping_cents)
                  )}
                </span>
              </div>
              {order.discount_cents > 0 && (
                <div className="flex justify-between text-sm border-t border-border pt-2">
                  <span className="text-muted-foreground">
                    Réduction {order.promo_code && `(${order.promo_code})`}
                  </span>
                  <span className="font-medium text-green-600">
                    -{formatPrice(order.discount_cents)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-3 border-t border-border mt-3">
                <span className="text-foreground">Total</span>
                <span className="text-primary font-mono">{formatPrice(order.total_cents)}</span>
              </div>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
          >
            <Button size="lg" variant="outline" asChild>
              <Link to="/">Retour à l'accueil</Link>
            </Button>
            <Button size="lg" asChild>
              <Link to="/looks">
                Continuer mes achats
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </StoreLayout>
  );
}
